import { Link, Outlet, createRootRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { apiFetch, type Me } from "../lib/api";

function Layout() {
  const [me, setMe] = useState<Me | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showDev, setShowDev] = useState(false);

  const reload = () => {
    setMe(null); setErr(null);
    apiFetch<Me>("/api/me").then(setMe).catch((e: Error) => setErr(e.message));
  };
  useEffect(reload, []);

  const saveDev = (token: string, role: string, email: string) => {
    localStorage.setItem("skids.devToken", token);
    localStorage.setItem("skids.devRole", role);
    localStorage.setItem("skids.devEmail", email);
    setShowDev(false);
    reload();
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-semibold text-slate-900">SKIDS Manager CRM</span>
            <span className="text-xs text-slate-400">Phase 1 · staging</span>
          </div>
          <div className="flex items-center gap-3">
            <ManagerSearch />
            <div className="text-xs text-slate-500 flex items-center gap-2">
              {me ? (
                <>
                  <span>{me.email}</span>
                  <span className="rounded bg-slate-900 text-white px-1.5 py-0.5">
                    {me.role}
                  </span>
                </>
              ) : err ? (
                <span className="text-red-600">{err}</span>
              ) : (
                <span className="text-slate-400">loading…</span>
              )}
              <button
                onClick={() => setShowDev((v) => !v)}
                className="text-xs text-slate-400 hover:text-slate-700 underline"
                title="Dev auth"
              >
                dev
              </button>
            </div>
          </div>
        </div>
        <nav className="mx-auto max-w-7xl px-6 pb-3 flex gap-1 overflow-x-auto">
          <Link to="/" className="nav-link" activeProps={{ className: "nav-link nav-link-active" }}>
            Today
          </Link>
          <Link
            to="/walk-in"
            className="nav-link"
            activeProps={{ className: "nav-link nav-link-active" }}
          >
            Walk-In
          </Link>
          <Link
            to="/followups"
            className="nav-link"
            activeProps={{ className: "nav-link nav-link-active" }}
          >
            Follow-Ups
          </Link>
          <Link
            to="/schools"
            className="nav-link"
            activeProps={{ className: "nav-link nav-link-active" }}
          >
            Schools
          </Link>
          <Link
            to="/kpis"
            className="nav-link"
            activeProps={{ className: "nav-link nav-link-active" }}
          >
            KPIs
          </Link>
          {me?.role === "admin" && (
            <Link
              to="/audit"
              className="nav-link"
              activeProps={{ className: "nav-link nav-link-active" }}
            >
              Audit
            </Link>
          )}
          <Link
            to="/settings"
            className="nav-link"
            activeProps={{ className: "nav-link nav-link-active" }}
          >
            Settings
          </Link>
        </nav>
      </header>
      {showDev && (
        <DevAuthPanel
          onSave={saveDev}
          initial={{
            token: localStorage.getItem("skids.devToken") ?? "",
            role: localStorage.getItem("skids.devRole") ?? "manager",
            email:
              localStorage.getItem("skids.devEmail") ??
              "manager@skids.clinic",
          }}
        />
      )}
      <main className="mx-auto max-w-7xl w-full p-6 flex-1">
        <Outlet />
      </main>
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-3 text-xs text-slate-400">
          SKIDS Bangalore HSR · {new Date().getFullYear()}
        </div>
      </footer>
    </div>
  );
}

function ManagerSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<
    Array<{ familyId: string; parentName?: string; parentPhone?: string; childNames: string[] }>
  >([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await apiFetch<{ results: typeof results }>(
          `/api/manager-search?q=${encodeURIComponent(q.trim())}`,
        );
        setResults(r.results);
        setOpen(true);
      } catch { /* swallow */ }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="relative">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search phone or name…"
        className="text-sm border border-slate-300 rounded px-2 py-1 w-56 focus:outline-none focus:ring-2 focus:ring-slate-300"
      />
      {open && results.length > 0 && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-slate-200 rounded shadow-lg z-50">
          {results.map((r) => (
            <button
              key={r.familyId}
              onClick={() => {
                setOpen(false);
                setQ("");
                navigate({ to: "/family/$familyId", params: { familyId: r.familyId } });
              }}
              className="block w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b border-slate-100 last:border-b-0"
            >
              <div className="font-medium text-slate-900">{r.parentName ?? "—"}</div>
              <div className="text-xs text-slate-500">
                {r.parentPhone}
                {r.childNames.length > 0 && ` · ${r.childNames.slice(0, 2).join(", ")}`}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DevAuthPanel({
  onSave,
  initial,
}: {
  onSave: (token: string, role: string, email: string) => void;
  initial: { token: string; role: string; email: string };
}) {
  const [token, setToken] = useState(initial.token);
  const [role, setRole] = useState(initial.role);
  const [email, setEmail] = useState(initial.email);
  return (
    <div className="border-b border-amber-300 bg-amber-50">
      <div className="mx-auto max-w-7xl px-6 py-3 flex flex-wrap gap-2 items-end text-sm">
        <span className="text-amber-900 font-medium">Dev auth (staging only)</span>
        <input
          className="border border-amber-300 rounded px-2 py-1 flex-1 min-w-[260px]"
          placeholder="X-Dev-Token"
          value={token}
          onChange={(e) => setToken(e.target.value)}
        />
        <select
          className="border border-amber-300 rounded px-2 py-1"
          value={role}
          onChange={(e) => setRole(e.target.value)}
        >
          <option value="admin">admin</option>
          <option value="manager">manager</option>
          <option value="doctor">doctor</option>
          <option value="junior">junior</option>
        </select>
        <input
          className="border border-amber-300 rounded px-2 py-1"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <button
          onClick={() => onSave(token, role, email)}
          className="rounded bg-amber-600 text-white px-3 py-1"
        >
          Save
        </button>
      </div>
    </div>
  );
}

export const Route = createRootRoute({ component: Layout });
